import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface AiPromptInputProps {
	onSend: (text: string) => void;
	isLoading?: boolean;
	disabled?: boolean;
	placeholder?: string;
	autoFocus?: boolean;
	className?: string;
}

/**
 * Auto-growing textarea with a send button. Enter sends, Shift+Enter inserts
 * a newline. Stays editable while a reply is loading so the next question
 * can be typed, but does not submit until the reply has landed.
 */
const AiPromptInput: React.FC<AiPromptInputProps> = ({
	onSend,
	isLoading,
	disabled,
	placeholder = "Fråga om uppdrag, kunder, tidrapporter…",
	autoFocus,
	className,
}) => {
	const [text, setText] = useState("");
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const canSend = !disabled && !isLoading && text.trim().length > 0;

	const resize = useCallback(() => {
		const element = textareaRef.current;
		if (!element) return;
		element.style.height = "0px";
		element.style.height = Math.min(element.scrollHeight, 180) + "px";
	}, []);

	useEffect(() => {
		resize();
	}, [text, resize]);

	useEffect(() => {
		if (autoFocus) textareaRef.current?.focus();
	}, [autoFocus]);

	const submit = () => {
		if (!canSend) return;
		onSend(text.trim());
		setText("");
	};

	return (
		<form
			className={cn(
				"flex items-end gap-2 rounded-xl border border-border-strong bg-white p-2 shadow-sm focus-within:border-brand focus-within:ring-1 focus-within:ring-brand",
				disabled && "opacity-60",
				className
			)}
			onSubmit={(event) => {
				event.preventDefault();
				submit();
			}}
		>
			<textarea
				ref={textareaRef}
				value={text}
				rows={1}
				disabled={disabled}
				placeholder={placeholder}
				onChange={(event) => setText(event.target.value)}
				onKeyDown={(event) => {
					if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
						event.preventDefault();
						submit();
					}
				}}
				className="max-h-[180px] min-h-[24px] flex-1 resize-none bg-transparent px-1 py-1 text-sm leading-6 text-foreground placeholder:text-foreground-subtle focus:outline-none"
			/>
			<button
				type="submit"
				disabled={!canSend}
				aria-label="Skicka"
				className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-neutral-800 text-white transition-colors hover:bg-neutral-700 disabled:bg-neutral-200 disabled:text-neutral-500"
			>
				{isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
			</button>
		</form>
	);
};

export default AiPromptInput;
